import type { FormEvent } from "react";
import { Field, IconButton, Modal, TextInput } from "@carbon6/c6-frontend-design";
import { checkSourceRef } from "../../feature/sourceRef";
import type { DatasetListDialogState } from "../DatasetListPageTypes";
import styles from "./RegisterDatasetModal.module.css";

type RegisterDatasetModalProps = {
  dialog: Extract<DatasetListDialogState, { kind: "register" }>;
  onClose: () => void;
  onSourceRefChange: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
};

/**
 * 원본 주소를 조직에 등록하는 모달.
 *
 * 표현만 한다 — 값 · 오류 · 제출 상태는 페이지가 든다. 검증은 제출 전에 클라이언트가 먼저 보고
 * (250ms 왕복을 오타 한 번에 쓰지 않는다), 서버가 그래도 거부하면 그 응답이 이긴다.
 */
export function RegisterDatasetModal({
  dialog,
  onClose,
  onDisplayNameChange,
  onSourceRefChange,
  onSubmit,
}: RegisterDatasetModalProps) {
  const { errorMessage, fieldErrors, form, submitting } = dialog;
  const check = checkSourceRef(form.sourceRef);
  // 서버가 저장할 문자열을 미리 보인다 — 양끝 `/` 같은 차이를 등록 뒤에 발견하지 않게.
  const normalizedPreview =
    check.ok && check.normalized !== form.sourceRef.trim() ? check.normalized : null;

  return (
    <Modal
      open
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      size="md"
      onClose={onClose}
    >
      <Modal.Header>
        <Modal.Header.Start>
          <Modal.Header.Title>Dataset 등록</Modal.Header.Title>
        </Modal.Header.Start>
        <Modal.Header.End>
          <Modal.Header.Close disabled={submitting} label="닫기" />
        </Modal.Header.End>
      </Modal.Header>
      <form onSubmit={onSubmit}>
        <Modal.Body>
          <div className={styles.fields}>
            <div className={styles.field}>
              <Field label="원본 주소" layout="stack" disabled={submitting}>
                <TextInput
                  fullWidth
                  invalid={fieldErrors.sourceRef !== null}
                  placeholder="az://huggingface/RoboCOIN/<repo>"
                  value={form.sourceRef}
                  onChange={(event) => onSourceRefChange(event.target.value)}
                />
              </Field>
              {fieldErrors.sourceRef === null ? (
                normalizedPreview === null ? null : (
                  <p className={styles.hint}>저장되는 주소: {normalizedPreview}</p>
                )
              ) : (
                <p className={styles.fieldError} role="alert">
                  {fieldErrors.sourceRef}
                </p>
              )}
            </div>

            <div className={styles.field}>
              <Field label="표시 이름" layout="stack" disabled={submitting}>
                <TextInput
                  fullWidth
                  invalid={fieldErrors.displayName !== null}
                  maxLength={255}
                  placeholder="목록에 보일 이름"
                  value={form.displayName}
                  onChange={(event) => onDisplayNameChange(event.target.value)}
                />
              </Field>
              {fieldErrors.displayName === null ? null : (
                <p className={styles.fieldError} role="alert">
                  {fieldErrors.displayName}
                </p>
              )}
            </div>

            <p className={styles.hint}>
              현재는 Azure prefix 원본만 등록할 수 있습니다. 이미 등록된 주소를 다시 넣으면 기존
              Dataset 을 열고 표시 이름은 바뀌지 않습니다.
            </p>
            {errorMessage === null ? null : (
              <p className={styles.formError} role="alert">
                {errorMessage}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={styles.actions}>
            <IconButton
              disabled={submitting}
              text="취소"
              tone="neutral"
              type="button"
              variant="surface"
              onClick={onClose}
            />
            <IconButton
              disabled={submitting}
              text={submitting ? "등록 중" : "등록"}
              tone="primary"
              type="submit"
              variant="solid"
            />
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
